import React, { useEffect, useMemo } from 'react';

import {
  LTButton,
  LTCopyToClipboard,
  LTText,
} from '@lambdatestincprivate/lt-components';
import { themeGet } from '@primer/react';
import styled from 'styled-components';

import HistoryList from './HistoryList';
import { useTestHistory } from './useTestHistory';
import { getEnvVar } from '../../utils/env';
import { getCookieData } from '../../utils/cookieHandler/handler';
import { getUrlByUserDomain } from '../../utils/utilityFunctions';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const IdRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FooterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
  gap: 8px;
  padding: 16px 0px 16px 16px;
  height: 64px;
  border-top: 1px solid;
  border-color: ${themeGet('colors.border.default')};
`;

function resolveApiBaseUrl() {
  const prefix = getEnvVar('REACT_APP_API_BASE_URL_PREFIX');
  if (prefix) {
    return getUrlByUserDomain(prefix);
  }
  return getEnvVar('REACT_APP_INSIGHTS_API_URL') || '';
}

export function resolveAnalyticsUrl() {
  const prefix = getEnvVar('REACT_APP_ML_ANALYTICS_PREFIX');
  if (prefix) {
    return getUrlByUserDomain(prefix);
  }
  return getEnvVar('REACT_APP_MAGICLEAP_URL') || '';
}

function resolveAuthToken() {
  const cookieName =
    getEnvVar('REACT_APP_COOKIE_NAME') ||
    getEnvVar('REACT_APP_JWT_COOKIE_NAME') ||
    '';
  return getCookieData(cookieName) || '';
}

/**
 * Self-contained Test History component.
 * Resolves API base URL and auth token from env/cookies internally.
 * No Redux dependency.
 *
 * @param {Object}  props
 * @param {Object}  props.testDetail - Test detail (needs id, name, type, mode, feature_flag)
 */
const TestHistory = ({ testDetail }) => {
  const apiBaseUrl = useMemo(() => resolveApiBaseUrl(), []);
  const authToken = useMemo(() => resolveAuthToken(), []);
  const analyticsUrl = useMemo(() => resolveAnalyticsUrl(), []);

  const {
    data,
    isLoading,
    isSilentLoading,
    hasMore,
    fetchHistory,
    loadMore,
  } = useTestHistory({ testDetail, apiBaseUrl, authToken });

  const { id = '', name: testName = '' } = testDetail || {};

  useEffect(() => {
    if (testDetail && apiBaseUrl && authToken) {
      fetchHistory(0, false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only re-fetch when test ID changes
  }, [testDetail?.id]);

  const showFooter = !isLoading && data.length > 0 && analyticsUrl;

  return (
    <Container>
      <ItemContainer>
        <IdRow>
          <LTText
            variant={LTText.TextVariant.SUBHEADER_REGULAR}
            color="fg.muted"
          >
            {`Test ID: ${id}`}
          </LTText>
          <LTCopyToClipboard textToCopy={id} />
        </IdRow>
        <LTText
          variant={LTText.TextVariant.SUBHEADER_BOLD}
          style={{ wordBreak: 'break-word' }}
        >
          {testName || id}
        </LTText>
      </ItemContainer>
      <HistoryList
        data={data}
        isLoading={isLoading}
        isSilentLoading={isSilentLoading}
        hasMore={hasMore}
        parentTestId={id}
        onLoadMore={loadMore}
      />
      {showFooter && (
        <FooterContainer>
          <LTButton
            onClick={() => {
              window.open(
                `${analyticsUrl}/test-insights/${btoa(testName || id)}`,
              );
            }}
            size={LTButton.Size.MEDIUM}
          >
            View more insights
          </LTButton>
        </FooterContainer>
      )}
    </Container>
  );
};

export default TestHistory;
